import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, IconButton, Select, MenuItem } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SectionLabel from '../Common/SectionLabel';
import { OUTREACH_TOPICS } from 'features/IRWorkstation/constants/outreachTopics';
import useGenerateDraft from 'hooks/useGenerateDraft';
import { useNotificationContext } from 'contexts/NotificationContext';

const EmailField = ({ label, value }) => {
	if (!value) return null;

	return (
		<Box
			sx={{
				display: 'flex',
				gap: '10px',
				py: '2px',
				alignItems: 'flex-start',
			}}
		>
			<Typography
				sx={{
					fontSize: '12px',
					color: 'text.secondary',
					width: 56,
					flexShrink: 0,
				}}
			>
				{label}
			</Typography>
			<Typography sx={{ fontSize: '12px', color: 'text.primary' }}>
				{value}
			</Typography>
		</Box>
	);
};

EmailField.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
};

/**
 * Lets the user pick an outreach topic and generate an AI-drafted LP email for
 * the record an alert points at. Rendered only for alerts whose Salesforce
 * object the drafting endpoint supports - see resolveDraftOutreachTarget in
 * AlertsDrawer.
 */
const DraftOutreachSection = ({
	tenantId,
	salesforceObjectType,
	salesforceObjectId,
}) => {
	const [topic, setTopic] = useState('');

	const { generate, reset, draft, loading } = useGenerateDraft();
	const { showNotification } = useNotificationContext();

	// Drop any existing draft: it was generated for the previous topic, and
	// leaving it on screen would misrepresent what the dropdown now says.
	const handleTopicChange = (event) => {
		setTopic(event.target.value);
		reset();
	};

	const handleGenerate = async () => {
		try {
			await generate({
				tenantId,
				topic,
				salesforceObjectType,
				salesforceObjectId,
			});
		} catch (error) {
			// The hook surfaces the service's user-safe message.
			showNotification(
				error.message || 'Failed to generate outreach draft',
				'error'
			);
		}
	};

	const recipient = draft?.contact?.email
		? `${draft.contact.name} <${draft.contact.email}>`
		: draft?.contact?.name;

	// Only reachable from the button inside the rendered card, so `draft` is set.
	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(
				`To: ${recipient}\nSubject: ${draft.email.subject}\n\n${draft.email.body}`
			);
			showNotification('Email copied', 'success');
		} catch (error) {
			console.error('Failed to copy email:', error);
			showNotification('Failed to copy email', 'error');
		}
	};

	return (
		<Box>
			<SectionLabel text={'Draft Outreach'} />

			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<Select
					value={topic}
					onChange={handleTopicChange}
					displayEmpty
					size='small'
					disabled={loading}
					sx={{ flex: 1, fontSize: '12px' }}
					inputProps={{ 'aria-label': 'Outreach type' }}
				>
					<MenuItem value='' disabled sx={{ fontSize: '12px' }}>
						Select outreach type
					</MenuItem>
					{OUTREACH_TOPICS.map((option) => (
						<MenuItem
							key={option.value}
							value={option.value}
							sx={{ fontSize: '12px' }}
						>
							{option.label}
						</MenuItem>
					))}
				</Select>

				<LoadingButton
					variant='outlined'
					size='small'
					onClick={handleGenerate}
					loading={loading}
					disabled={!topic || loading}
					sx={{
						fontSize: '12px',
						fontWeight: 500,
						padding: '7px 14px',
						borderRadius: '6px',
						backgroundColor: 'status.blue.bg',
						color: 'status.blue.text',
						borderColor: 'status.blue.border',
						cursor: 'pointer',
						flexShrink: 0,
						'&:hover': { backgroundColor: 'status.blue.hover' },
						// Explicit disabled styling: the sx backgroundColor above would
						// otherwise override MUI's default and leave the disabled button
						// looking enabled. Excludes the loading state, which is also
						// disabled but should keep its blue treatment and spinner.
						'&.Mui-disabled:not(.MuiLoadingButton-loading)': {
							backgroundColor: 'background.subtle',
							color: 'text.tertiary',
							borderColor: 'border.light',
						},
					}}
				>
					{loading ? 'Generating...' : 'Generate'}
				</LoadingButton>
			</Box>

			{draft?.email && (
				<Box
					sx={{
						position: 'relative',
						mt: 1,
						backgroundColor: 'background.paper',
						borderRadius: '8px',
						border: '0.5px solid',
						borderColor: 'border.light',
						overflow: 'hidden',
					}}
				>
					<IconButton
						data-testid='copy-outreach-email-button'
						aria-label='Copy email'
						size='small'
						onClick={handleCopy}
						sx={{
							position: 'absolute',
							top: 8,
							right: 8,
							width: 26,
							height: 26,
							borderRadius: '6px',
							border: '0.5px solid',
							borderColor: 'border.light',
							backgroundColor: 'background.paper',
							color: 'text.secondary',
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<ContentCopyIcon sx={{ fontSize: 14 }} />
					</IconButton>

					{/* To / Subject header strip */}
					<Box
						sx={{
							backgroundColor: 'background.paper',
							borderBottom: '0.5px solid',
							borderColor: 'border.light',
							padding: '8px 10px',
							pr: '44px',
						}}
					>
						<EmailField label={'To'} value={recipient} />
						<EmailField label={'Subject'} value={draft.email.subject} />
					</Box>

					{/* Email body */}
					<Box
						sx={{ backgroundColor: 'background.subtle', padding: '10px 12px' }}
					>
						<Typography
							sx={{
								fontSize: '12px',
								color: 'text.primary',
								whiteSpace: 'pre-wrap',
							}}
						>
							{draft.email.body}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

DraftOutreachSection.propTypes = {
	tenantId: PropTypes.string.isRequired,
	salesforceObjectType: PropTypes.string.isRequired,
	salesforceObjectId: PropTypes.string.isRequired,
};

export default DraftOutreachSection;
